import React, { useRef, useState, useEffect } from "react";
import { FiEye, FiRepeat, FiTrash } from "react-icons/fi";
import { validateImageFile } from "./imageUtils";
import { ImageUploadPlaceholder } from "./ImageUploadPlaceholder";
import { ImagePreviewModal } from "./ImagePreviewModal";
import FallbackImage from "components/FallbackImage/FallbackImage";

export interface ImageTileProps {
	compact?: boolean;
	hasError?: boolean;
	image: File | string | null;
	label?: string;
	modalTitleOverride?: string;
	onChange: (file: File | null) => void;
}

export const ImageTile: React.FC<ImageTileProps> = ({
	image,
	modalTitleOverride,
	onChange,
	label,
	hasError = false,
	compact = false,
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [inlineError, setInlineError] = useState<string | null>(null);
	const [isViewOpen, setIsViewOpen] = useState(false);
	const [isDragOver, setIsDragOver] = useState(false);

	useEffect(() => {
		if (image instanceof File) {
			const url = URL.createObjectURL(image);
			setPreviewUrl(url);
			return () => URL.revokeObjectURL(url);
		}
		if (typeof image === "string" && image) {
			setPreviewUrl(image);
		} else {
			setPreviewUrl(null);
		}
	}, [image]);

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		const result = validateImageFile(file);
		if (!result.valid) {
			setInlineError(result.error);
			return;
		}

		setInlineError(null);
		onChange(file);
	};

	const handleDropFile = (file: File) => {
		const result = validateImageFile(file);
		if (!result.valid) {
			setInlineError(result.error);
			return;
		}
		setInlineError(null);
		setPreviewUrl(URL.createObjectURL(file));
		onChange(file);
	};

	const handleClear = () => {
		setInlineError(null);
		onChange(null);
		if (inputRef.current) {
			inputRef.current.value = "";
		}
	};

	// --- Drag-to-upload on the "has image" tile (replaces current image) ---
	const handleDragEnter = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.dataTransfer.types.includes("Files")) {
			setIsDragOver(true);
		}
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (
			e.currentTarget === e.target ||
			!e.currentTarget.contains(e.relatedTarget as Node)
		) {
			setIsDragOver(false);
		}
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(false);
		const multiDrop = e.dataTransfer.files?.length > 1;
		const file = e.dataTransfer.files?.[0];
		if (file) {
			handleDropFile(file);
		}
		if (multiDrop) {
			setInlineError("Only one image is supported. The first image was used.");
		}
	};

	const showError = !!inlineError || hasError;

	if (!previewUrl) {
		return (
			<>
				<ImageUploadPlaceholder
					hasError={showError}
					onClick={() => inputRef.current?.click()}
					onDrop={(files) => {
						handleDropFile(files[0]);
						if (files.length > 1) {
							setInlineError("Only one image is supported. The first image was used.");
						}
					}}
					className={compact ? "min-h-[140px] max-h-[140px]" : ""}
				/>
				<input
					ref={inputRef}
					type="file"
					accept="image/jpeg,image/png"
					className="hidden"
					onChange={handleFileSelect}
				/>
				{inlineError && (
					<p className="mt-1 text-xs text-red-500">{inlineError}</p>
				)}
			</>
		);
	}

	return (
		<>
			<div
				className={`relative cursor-[inherit] rounded-lg border-2 border-dashed p-3 transition-colors overflow-hidden ${compact
					? "min-h-[140px] max-h-[140px]"
					: "min-h-[155px] max-h-[155px]"
					} ${isDragOver
						? "border-blue-400 bg-blue-50"
						: showError
							? "border-red-400 bg-red-50"
							: "border-gray-300 bg-gray-50 hover:border-gray-400"
					}`}
				onClick={() => setIsViewOpen(true)}
				onDragEnter={handleDragEnter}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
			>
				{isDragOver && (
					<div className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-blue-50/90">
						<span className="text-sm font-medium text-blue-600">
							Drop to replace this image
						</span>
					</div>
				)}

				{/* Desktop: image left, buttons right */}
				<div className="hidden sm:flex sm:items-center sm:gap-4 h-full">
					<FallbackImage src={previewUrl} alt={label || "Preview"} className={`rounded object-contain ${compact
						? "max-h-[100px] max-w-[120px]"
						: "max-h-[115px] max-w-[160px]"
						}`} />
					<div
						className="flex cursor-default flex-col gap-1.5"
						onPointerDown={(e) => e.stopPropagation()}
					>
						<button
							type="button"
							className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
							onClick={(e) => {
								e.stopPropagation();
								setIsViewOpen(true);
							}}
						>
							<FiEye size={14} />
							Preview
						</button>
						<button
							type="button"
							className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
							onClick={(e) => {
								e.stopPropagation();
								inputRef.current?.click();
							}}
						>
							<FiRepeat size={14} />
							Replace
						</button>
						<button
							type="button"
							className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm text-red-500 hover:bg-red-50"
							onClick={(e) => {
								e.stopPropagation();
								handleClear();
							}}
						>
							<FiTrash size={14} />
							Remove
						</button>
					</div>
				</div>

				{/* Mobile: image top, buttons full-width below */}
				<div className="flex flex-col items-center gap-2 sm:hidden">
					<img
						src={previewUrl}
						alt={label || "Preview"}
						className="max-h-[60px] max-w-full rounded object-contain"
					/>
					<div className="flex w-full cursor-default gap-1" onPointerDown={(e) => e.stopPropagation()}>
						<button
							type="button"
							className="flex flex-1 items-center justify-center gap-1 rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-600 hover:bg-gray-100"
							onClick={(e) => {
								e.stopPropagation();
								setIsViewOpen(true);
							}}
						>
							<FiEye size={12} />
							Preview
						</button>
						<button
							type="button"
							className="flex flex-1 items-center justify-center gap-1 rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-600 hover:bg-gray-100"
							onClick={(e) => {
								e.stopPropagation();
								inputRef.current?.click();
							}}
						>
							<FiRepeat size={12} />
							Replace
						</button>
						<button
							type="button"
							className="flex flex-1 items-center justify-center gap-1 rounded-md border border-red-200 px-2 py-1.5 text-xs text-red-500 hover:bg-red-50"
							onClick={(e) => {
								e.stopPropagation();
								handleClear();
							}}
						>
							<FiTrash size={12} />
							Remove
						</button>
					</div>
				</div>
			</div>

			<input
				ref={inputRef}
				type="file"
				accept="image/jpeg,image/png"
				className="hidden"
				onChange={handleFileSelect}
			/>

			{inlineError && (
				<p className="mt-1 text-xs text-red-500">{inlineError}</p>
			)}

			<ImagePreviewModal
				isOpen={isViewOpen}
				onClose={() => setIsViewOpen(false)}
				previewUrl={previewUrl}
				title={modalTitleOverride ?? label ?? "Image Preview"}
			/>
		</>
	);
};
